import { MediaPlan } from '../../types'
import { motion } from 'framer-motion'
import { DollarSign, Target, BarChart3, Calendar, Layers, TrendingUp, Globe, Zap } from 'lucide-react'

interface MediaPlannerProps {
  data: MediaPlan
}

export function MediaPlanner({ data }: MediaPlannerProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading media plan...</p>
        </div>
      </div>
    )
  }

  // Helper to format numbers
  const formatNumber = (num: number | string | undefined) => {
    if (num === undefined || num === null) return '0'
    const n = typeof num === 'string' ? parseFloat(num) : num
    if (isNaN(n)) return num.toString()
    return n.toLocaleString()
  }

  // Helper to format currency
  const formatCurrency = (num: number | string | undefined) => {
    if (num === undefined || num === null) return '$0'
    const n = typeof num === 'string' ? parseFloat(num) : num
    if (isNaN(n)) return `$${num}`
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
  }

  // Get channel colors
  const getChannelColor = (channel: string) => {
    const colors: { [key: string]: string } = {
      'Display': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Social': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Video': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'Search': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      'Programmatic': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    }
    return colors[channel] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg shadow-sm p-4 lg:p-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg lg:text-xl font-bold text-white mb-1">
              {data.campaign_name || 'Media Plan'}
            </h2>
            {data.advertiser && (
              <p className="text-sm text-gray-300">
                <span className="text-gray-400">Advertiser:</span> {data.advertiser}
                {data.market && <span className="ml-3"><span className="text-gray-400">Market:</span> {data.market}</span>}
              </p>
            )}
          </div>
          {data.campaign_period && (
            <div className="flex items-center text-sm text-gray-300 bg-gray-800/50 px-3 py-1.5 rounded-lg">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              {data.campaign_period}
            </div>
          )}
        </div>
        
        {/* Summary */}
        {data.summary && (
          <p className="text-sm text-gray-300 mt-3 leading-relaxed">{data.summary}</p>
        )}
        
        {/* Channels & Creative Types Tags */}
        <div className="flex flex-wrap gap-2 mt-4">
          {Array.isArray(data.channels) && data.channels.map((channel, index) => (
            <span 
              key={index} 
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${getChannelColor(typeof channel === 'string' ? channel : channel.name)}`}
            >
              {typeof channel === 'string' ? channel : channel.name}
            </span>
          ))}
        </div>
      </motion.div>

      {/* KPIs Summary */}
      {data.kpis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-primary-500" />
            Projected KPIs
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(data.kpis.impressions)}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Impressions</div>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-xl font-bold text-green-600 dark:text-green-400">{formatNumber(data.kpis.clicks)}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Clicks</div>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{data.kpis.ctr}%</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">CTR</div>
            </div>
            <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-xl font-bold text-orange-600 dark:text-orange-400">{data.kpis.reach_estimate}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Reach</div>
            </div>
            <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{data.kpis.frequency}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Frequency</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Budget Allocation */}
      {data.budget_allocation && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-green-500" />
              Budget Allocation
            </h3>
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              Total: {formatCurrency(data.budget_allocation.total_cost)}
            </div>
          </div>
          <div className="space-y-3">
            {data.budget_allocation.by_channel.map((allocation, index) => {
              const percentage = (allocation.cost / data.budget_allocation!.total_cost) * 100
              return (
                <div key={index} className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getChannelColor(allocation.channel)}`}>
                      {allocation.channel}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(allocation.cost)} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* Legacy Budget Allocation Format */}
      {!data.budget_allocation && data.budgetAllocation && data.budgetAllocation.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-green-500" />
            Budget Allocation
          </h3>
          <div className="space-y-3">
            {data.budgetAllocation.map((allocation, index) => (
              <div key={index} className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">{allocation.channel}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{allocation.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 mb-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${allocation.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{allocation.rationale}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(allocation.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Media Placements */}
      {data.placements && data.placements.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Layers className="h-5 w-5 mr-2 text-purple-500" />
            Media Placements
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Channel</th>
                  <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Format</th>
                  <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">CPM</th>
                  <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Impressions</th>
                  <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.placements.map((placement, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50">
                    <td className="py-3 px-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getChannelColor(placement.channel)}`}>
                        {placement.channel}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-gray-900 dark:text-white font-medium">{placement.format}</td>
                    <td className="py-3 px-3 text-right text-gray-600 dark:text-gray-400">{formatCurrency(placement.cpm)}</td>
                    <td className="py-3 px-3 text-right text-gray-600 dark:text-gray-400">{formatNumber(placement.impressions)}</td>
                    <td className="py-3 px-3 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(placement.cost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-slate-800">
                  <td colSpan={3} className="py-3 px-3 font-semibold text-gray-900 dark:text-white">Total</td>
                  <td className="py-3 px-3 text-right font-semibold text-gray-900 dark:text-white">
                    {formatNumber(data.placements.reduce((sum, p) => sum + p.impressions, 0))}
                  </td>
                  <td className="py-3 px-3 text-right font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(data.placements.reduce((sum, p) => sum + p.cost, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          {/* Placement Details (Rationale) */}
          <div className="mt-4 space-y-2">
            {data.placements.map((placement, index) => (
              placement.rationale && (
                <div key={index} className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg text-sm">
                  <div className="flex items-start">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getChannelColor(placement.channel)} mr-2 flex-shrink-0`}>
                      {placement.format}
                    </span>
                    <p className="text-gray-600 dark:text-gray-400">{placement.rationale}</p>
                  </div>
                </div>
              )
            ))}
          </div>
        </motion.div>
      )}

      {/* Targeting */}
      {data.targeting && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <Target className="h-5 w-5 mr-2 text-red-500" />
            Targeting Strategy
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{data.targeting}</p>
        </motion.div>
      )}

      {/* Creative Types & Sizes */}
      {(data.creative_type && data.creative_type.length > 0) || (data.creative_sizes && data.creative_sizes.length > 0) ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-indigo-500" />
            Creative Specifications
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {data.creative_type && data.creative_type.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Creative Types</h4>
                <div className="flex flex-wrap gap-2">
                  {data.creative_type.map((type, index) => (
                    <span key={index} className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 rounded text-xs font-medium">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.creative_sizes && data.creative_sizes.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ad Sizes</h4>
                <div className="flex flex-wrap gap-2">
                  {data.creative_sizes.map((size, index) => (
                    <span key={index} className="px-2.5 py-1 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded text-xs font-medium">
                      {size}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ) : null}

      {/* Strategic Recommendation */}
      {data.strategic_recommendation && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg shadow-sm border border-primary-200 dark:border-primary-800 p-4"
        >
          <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100 mb-3 flex items-center">
            <Zap className="h-5 w-5 mr-2 text-primary-500" />
            Strategic Recommendation
          </h3>
          <p className="text-sm text-primary-800 dark:text-primary-200 leading-relaxed">{data.strategic_recommendation}</p>
        </motion.div>
      )}

      {/* Legacy Channels (detailed format) */}
      {Array.isArray(data.channels) && data.channels.length > 0 && typeof data.channels[0] === 'object' && 'description' in data.channels[0] && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2 text-blue-500" />
            Media Channels
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data.channels as any[]).map((channel, index) => (
              <div key={index} className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">{channel.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    channel.type === 'digital' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                    channel.type === 'social' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                    channel.type === 'programmatic' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {channel.type}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{channel.description}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-500">Reach:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">{formatNumber(channel.reach)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-500">Frequency:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">{channel.frequency}x</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-500">Cost:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">{formatCurrency(channel.cost)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}
